'use client';

import { useEffect, useState } from 'react';
import { Star, Check, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/shared/ui/shadcn/dialog';
import { Button } from '@/shared/ui/shadcn/button';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { cn } from '@/shared/lib/cn';
import {
  appRatingService,
  type AppRatingForm,
} from '../api/app-rating.service';

const MAX_MESSAGE_LENGTH = 2000;

interface AppRatingDialogProps {
  open: boolean;
  /** Called with `false` when the user closes without answering ("Later"). */
  onOpenChange: (open: boolean) => void;
  /** Called once the answer is recorded, so the caller can stop asking. */
  onAnswered: () => void;
  /**
   * Pre-selects a score on open. Only the `?app-rating=N` preview passes this
   * — it is how the low-score branch (the reason chips, the part worth
   * looking at) can be reached without clicking through.
   */
  initialRating?: number | null;
}

/**
 * The satisfaction survey.
 *
 * Every visible string comes from GET /v1/app-rating/form — the question, the
 * star labels, the reasons, the thank-you line. Nothing is composed here: the
 * same dialog on Apple and Android must read identically, and a new reason has
 * to ship without a client release.
 *
 * Reasons appear only for a low score. They are the point of the survey: a
 * star rating says something is wrong, a reason code says what, and unlike free
 * text a code can be counted across users and watched week over week.
 */
export function AppRatingDialog({
  open,
  onOpenChange,
  onAnswered,
  initialRating = null,
}: AppRatingDialogProps) {
  const [form, setForm] = useState<AppRatingForm | null>(null);
  const [rating, setRating] = useState<number | null>(initialRating);
  const [reasons, setReasons] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [thanks, setThanks] = useState<string | null>(null);

  // Copy is fetched when the dialog opens, not on mount: for most sessions the
  // survey never appears, and its strings should not ride along on every page.
  useEffect(() => {
    if (!open || form) return;

    let cancelled = false;
    void appRatingService
      .getForm()
      .then((payload) => {
        if (!cancelled) setForm(payload);
      })
      .catch(() => {
        // Without copy there is no dialog to show. Close quietly rather than
        // render an empty box; the server gate will offer it again later.
        if (!cancelled) onOpenChange(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, form, onOpenChange]);

  const isLow =
    form !== null && rating !== null && rating <= form.low_rating_threshold;

  const toggleReason = (code: string) => {
    setReasons((current) =>
      current.includes(code)
        ? current.filter((item) => item !== code)
        : [...current, code]
    );
  };

  const handleSubmit = async () => {
    if (form === null || rating === null || submitting) return;

    setSubmitting(true);
    try {
      const thanksMessage = await appRatingService.submit({
        rating,
        ...(isLow && reasons.length > 0 ? { reason_codes: reasons } : {}),
        ...(message.trim() !== '' ? { message: message.trim() } : {}),
      });
      setThanks(thanksMessage);
      onAnswered();
    } finally {
      setSubmitting(false);
    }
  };

  // The thank-you state closes itself: keeping a dialog open after the user is
  // done just makes them find the X.
  useEffect(() => {
    if (thanks === null) return;

    const timer = setTimeout(() => onOpenChange(false), 1800);
    return () => clearTimeout(timer);
  }, [thanks, onOpenChange]);

  if (form === null) return null;

  const labelFor = (value: number): string =>
    form.rating_labels.find((entry) => entry.value === value)?.label ?? '';
  const ratingLabel = rating !== null ? labelFor(rating) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" showCloseButton={!submitting}>
        {thanks !== null ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <Check className="h-8 w-8 text-primary" />
            <DialogTitle className="text-list-item-title">
              {thanks}
            </DialogTitle>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{form.title}</DialogTitle>
              <DialogDescription>{form.subtitle}</DialogDescription>
            </DialogHeader>

            {/* No hover or focus preview: the dialog auto-focuses its first
                control on open, and a focus-driven preview lit one star before
                the user touched anything — reading as "1 star is already
                selected" and quietly anchoring the answer low. Stars fill only
                once chosen. */}
            <div className="flex flex-col items-center gap-2 py-2">
              <div className="flex items-center gap-1">
                {form.rating_labels.map(({ value }) => {
                  const filled = rating !== null && value <= rating;
                  return (
                    <button
                      key={value}
                      type="button"
                      aria-label={labelFor(value)}
                      aria-pressed={rating === value}
                      onClick={() => setRating(value)}
                      className="rounded-md p-1 transition-transform active:scale-90"
                    >
                      <Star
                        className={cn(
                          'h-8 w-8 transition-colors',
                          filled
                            ? 'fill-primary text-primary'
                            : 'text-muted-foreground/40'
                        )}
                      />
                    </button>
                  );
                })}
              </div>

              {/* Reserved line: without it the dialog jumps by the label's
                  height the moment a score is picked. */}
              <p className="text-form-help h-5 text-muted-foreground">
                {ratingLabel ?? ''}
              </p>
            </div>

            {rating !== null && (
              <div className="flex flex-col gap-3">
                <p className="text-form-label">
                  {isLow ? form.low_rating_prompt : form.high_rating_prompt}
                </p>

                {isLow && (
                  <div className="flex flex-wrap gap-2">
                    {form.reasons.map((reason) => {
                      const selected = reasons.includes(reason.code);
                      return (
                        <button
                          key={reason.code}
                          type="button"
                          aria-pressed={selected}
                          onClick={() => toggleReason(reason.code)}
                          className={cn(
                            'text-form-help rounded-full border px-3 py-1.5 transition-colors',
                            selected
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-border text-foreground hover:bg-accent'
                          )}
                        >
                          {reason.label}
                        </button>
                      );
                    })}
                  </div>
                )}

                <Textarea
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  maxLength={MAX_MESSAGE_LENGTH}
                  placeholder={
                    isLow
                      ? form.message_placeholder
                      : form.message_placeholder_high
                  }
                  className="min-h-[88px] resize-none"
                />
              </div>
            )}

            <div className="flex justify-end gap-2">
              {/* Filled secondary, not ghost: the same control is a filled
                  secondary capsule on Apple and Android, and this dialog is one
                  element on three platforms. */}
              <Button
                variant="secondary"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                {form.dismiss_label}
              </Button>
              <Button
                onClick={() => void handleSubmit()}
                disabled={rating === null || submitting}
              >
                {submitting && (
                  <Loader2 className="me-2 h-4 w-4 animate-spin" />
                )}
                {form.submit_label}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
